import { jsx as _jsx } from "react/jsx-runtime";
import { forwardRef, useCallback, useEffect, useMemo, useRef, useState, } from "react";
import DayPickerInputComponent from "react-day-picker/DayPickerInput";
import { DateUtils } from "react-day-picker";
import { css } from "@emotion/react";
import { format, isValid, parse, parseISO } from "date-fns";
import { inRange } from "lodash";
import { styled } from "../theme";
import { YearMonthPicker } from "../year-month-picker";
import { BaseInput } from "../form/atoms/TextualInput";
import { formatFieldValue } from "../form/text-field";
import { hasNativeDatepicker, isDateTypeSupported } from "./helpers";
import { CaptionForm, Container } from "./DatePicker";
import { DATE_FORMAT } from "./date";
export const DatePickerInput = forwardRef(({ inputProps, component: Component, value, range, style, testId, onChange, onDayChange, parseDate, formatDate, isDateTypeInput: fnIsDateTypeInput, hasNativeDatepicker: fnHasNativeDatepicker, FORMAT, IMPORTANT_MAX_DATE, }, ref) => {
    const pickerRef = useRef();
    const isDateTypeInput = fnIsDateTypeInput();
    const hasNativeDatepicker = fnHasNativeDatepicker();
    const props = {
        testId: `datepicker-input-${testId ?? ""}`,
        max: IMPORTANT_MAX_DATE,
        maxLength: IMPORTANT_MAX_DATE.length,
        hasNativeDatepicker,
        ...inputProps,
    };
    /**
     * We keep clone of the date, so we don't rely on parent storing the date and providing it back on every change.
     * Required to render the YearMonthSelect correctly.
     */
    const [dateClone, setDateClone] = useState(() => value ? parseISO(value) : undefined);
    const [yearMonthDateClone, setYearMonthDateClone] = useState(() => (value ? parseISO(value) : new Date()));
    const [valueClone, setValueClone] = useState(() => value
        ? isValid(parseISO(value)) && format(parseISO(value), FORMAT)
        : undefined);
    const setDate = useCallback((date) => {
        isDateTypeInput
            ? isValid(date) && setDateClone(date)
            : isValid(date) && setValueClone(format(date, FORMAT));
        isValid(date) && setYearMonthDateClone(date);
    }, [FORMAT, isDateTypeInput]);
    const inputValue = isDateTypeInput
        ? isValid(dateClone)
            ? format(dateClone, DATE_FORMAT.ISO_DATE)
            : ""
        : valueClone;
    useEffect(() => {
        if (value) {
            const date = parseISO(value);
            setDate(date);
        }
    }, [setDate, value]);
    const handleYearMonthChange = useCallback((date) => {
        if (isDateTypeInput) {
            // Update the year and month without changing the day
            const clonedDate = new Date(dateClone);
            clonedDate.setFullYear(date.getFullYear());
            clonedDate.setMonth(date.getMonth());
            setDateClone(clonedDate);
            setYearMonthDateClone(clonedDate);
        }
        else {
            setYearMonthDateClone(date);
        }
    }, [dateClone, isDateTypeInput]);
    const captionElement = useMemo(() => {
        /**
         * YearMonth component to be rendered in the date-picker-modal.
         * @param date - date currently selected in the date-picker-modal;
         * @returns JSX.Element - YearMonth picker component;
         */
        function YearMonth({ date }) {
            return (_jsx(CaptionForm, { className: "DayPicker-Caption", children: _jsx(YearMonthPicker, { date: date, range: range, tabIndex: -1, onChange: handleYearMonthChange }) }));
        }
        YearMonth.displayName = "YearMonth";
        return YearMonth;
    }, [range, handleYearMonthChange]);
    const dateProps = {
        type: "date",
        placeholder: `${format(new Date(), FORMAT)}`,
        onKeyDown: (event) => {
            if (event.key === "Escape") {
                event.currentTarget.blur();
                event.preventDefault();
            }
            if (event.key === "Backspace") {
                setDateClone(undefined);
                onChange("");
                event.currentTarget.blur();
                //NOTE: prevent from rendering of native datepicker after enter is pressed
                event.preventDefault();
            }
            if (event.key === "Enter") {
                const todayDateIsoString = format(new Date(), DATE_FORMAT.ISO_DATE);
                const formattedDateValue = isValid(parseISO(event.currentTarget.value))
                    ? event.currentTarget.value
                    : todayDateIsoString;
                setDateClone(parseISO(formattedDateValue));
                onChange(formattedDateValue);
                event.currentTarget.blur();
                //NOTE: prevent from rendering of native datepicker after enter is pressed
                event.preventDefault();
            }
        },
        ...props,
    };
    const textProps = {
        type: "tel",
        format: "date",
        placeholder: "mm/dd/yyyy",
        onKeyDown: (event) => {
            if (event.key === "Escape") {
                event.currentTarget.blur();
                event.preventDefault();
            }
            if (event.key === "Enter") {
                const date = new Date();
                const dateFromValue = parseDate(event.currentTarget.value, DATE_FORMAT.FULL_DATE);
                const formattedDateValue = isValid(dateFromValue)
                    ? format(dateFromValue, FORMAT)
                    : format(date, FORMAT);
                setValueClone(formattedDateValue);
                onChange(formattedDateValue);
                event.currentTarget.blur();
                //NOTE: prevent from rendering of native datepicker after enter is pressed
                event.preventDefault();
            }
        },
        onChange: (event) => {
            const { value } = event.currentTarget;
            const formattedValue = formatFieldValue(value, {
                type: "tel",
                capitalize: false,
                format: "date",
            });
            setValueClone(formattedValue);
            const date = parseDate(formattedValue, DATE_FORMAT.FULL_DATE);
            if (isValid(date)) {
                onChange(format(date, DATE_FORMAT.ISO_DATE));
            }
            else if (valueClone !== "") {
                onChange("/");
            }
        },
        ...props,
    };
    /* system native datepicker only when "type=date" is supported on mobile or when disabled */
    if (hasNativeDatepicker || props.disabled) {
        return (_jsx(Container, { ref: ref, style: style, "data-test-id": testId && `datepicker-${testId}`, children: _jsx(Component, { ...(isDateTypeInput ? dateProps : textProps), value: inputValue, disabled: props.disabled, onChange: (event) => {
                    const { value } = event.currentTarget;
                    setValueClone(value);
                    const date = parseDate(value, DATE_FORMAT.ISO_DATE);
                    if (isValid(date)) {
                        onChange(format(date, DATE_FORMAT.ISO_DATE));
                    }
                    else if (valueClone !== "") {
                        onChange("/");
                    }
                } }) }));
    }
    const date = isDateTypeInput
        ? dateClone
        : parseDate(valueClone, DATE_FORMAT.FULL_DATE);
    const handleDayChange = (day, DayModifiers, dayPickerInput) => {
        setDate(day);
        if (onDayChange) {
            onDayChange(day, DayModifiers, dayPickerInput);
        }
        if (onChange && isValid(day)) {
            onChange(format(day, DATE_FORMAT.ISO_DATE));
        }
    };
    const getValidYear = (date) => {
        const currentYear = new Date().getFullYear();
        const enteredYear = date.getFullYear();
        if (!inRange(enteredYear, currentYear - range.before[0], currentYear + range.after[0]))
            return currentYear - range.before[0];
        return enteredYear;
    };
    return (_jsx(Container, { ref: ref, style: style, "data-test-id": testId && `datepicker-${testId}`, children: _jsx(DayPickerInputComponent, { ref: pickerRef, component: Component, value: inputValue, inputProps: isDateTypeInput ? dateProps : textProps, format: isDateTypeInput ? DATE_FORMAT.ISO_DATE : FORMAT, formatDate: formatDate, parseDate: parseDate, onDayChange: handleDayChange, dayPickerProps: {
                month: yearMonthDateClone,
                selectedDays: [date],
                navbarElement: () => null,
                onDayClick: (date) => {
                    date.setFullYear(getValidYear(date));
                    setYearMonthDateClone(date);
                },
                onMonthChange: (date) => {
                    setYearMonthDateClone(date);
                },
                captionElement,
            } }) }));
});
export const parseDate = (value, formatString = DATE_FORMAT.ISO_DATE, locale) => {
    const parsed = parse(value, formatString, new Date(), { locale });
    if (value?.length === IMPORTANT_MAX_DATE.length && DateUtils.isDate(parsed)) {
        return parsed;
    }
    return undefined;
};
const formatDate = (date, formatString = DATE_FORMAT.ISO_DATE, locale) => {
    if (!date) {
        return "";
    }
    return format(date, formatString, { locale });
};
/**
 * This keeps the year 4 digit long. Do not remove.
 */
const IMPORTANT_MAX_DATE = "2999-12-31";
DatePickerInput.displayName = "DatePickerInput";
DatePickerInput.defaultProps = {
    /* hack for type='date' on mobile to displaying placeholder */
    component: styled(BaseInput) `
    ${({ value, hasNativeDatepicker }) => value?.length === 0 &&
        hasNativeDatepicker &&
        css `
        &[type="date"]:not(:focus):before {
          content: attr(placeholder);
        }
      `};

    ${({ value }) => 
    /** Change color of placeholder text to match rest of the app */
    value?.length === 0 &&
        css `
        color: rgba(24, 37, 45, 0.75);
      `};
  `,
    parseDate,
    formatDate,
    FORMAT: DATE_FORMAT.FULL_DATE,
    IMPORTANT_MAX_DATE,
    /*
     * use of type=date is preferable but on mobile and firefox the type=tel is used
     * the native datepicker would be displayed with our custom react-day-picker
     */
    isDateTypeInput: isDateTypeSupported,
    hasNativeDatepicker,
};
